import { useEffect } from 'react';
import { QueryResult, useApolloClient, useQuery } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import gql from 'graphql-tag';
import PubSub from 'pubsub-js';

import { BlockedFlags } from '../blocked';
import { getRootCause } from '../../apollo/errors';
import { PlayerCardOperator } from '../../types';

export type CardIdentifier = { cardNumber: string; cardOperator: PlayerCardOperator };

export default function useCardQuery({
  cardNumber,
  cardOperator,
  skip
}: CardIdentifier & { skip?: boolean }): QueryResult<Data> {
  const { error, refetch, ...restOfQuery } = useQuery<Data>(graphql, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    skip,
    variables: { where: { number: cardNumber, operator: cardOperator } }
  });

  useEffect(() => {
    function refetchCard() {
      refetch?.();
    }
    PubSub.subscribe('refetch-use-card-query', refetchCard);

    return () => {
      PubSub.unsubscribe(refetchCard);
    };
  }, [refetch]);

  return isHttp404Error(error) ? { ...restOfQuery, refetch, data: {} } : { error, refetch, ...restOfQuery };
}

export function useSearchCard() {
  const client = useApolloClient();
  return async function ({ cardNumber, cardOperator }: CardIdentifier & { skip?: boolean }) {
    const { errors, ...restOfQuery } = await client.query<Data>({
      query: graphql,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      variables: { where: { number: cardNumber, operator: cardOperator } }
    });
    return isHttp404Error({ graphQLErrors: errors } as ApolloError)
      ? { errors, ...restOfQuery, data: {} }
      : { errors, ...restOfQuery };
  };
}

function isHttp404Error(error?: ApolloError) {
  const { code } = getRootCause(error) || {};
  return code === 'HTTP-404';
}

export type Data = { card?: Card };

export type Card = {
  cardNumber: string;
  cardOperator: PlayerCardOperator;
  cardStatus: string;
  player?: Player;
  trackData?: {
    track1?: string;
    track2?: string;
    track3?: string;
  };
};

export type Player = BlockedFlags & {
  playerId: string;
  blockHistoryList: BlockHistory[];
  dateOfBirth: string | null;
  email: string | null;
  firstname: string | null;
  identifications: {
    idType: string;
    idLocation: string;
    idNumber: string;
    idExpiration: string;
  }[];
  rewardPoints?: number;
  racingRewardPoints?: number;
  surname: string | null;
  primaryIdentifier: string;
  rewardsDetail?: RewardsDetail;
};

export type RewardsDetail = {
  rcBalance?: number;
  tierCode: RewardsTierCode;
  tierDesc: string;
  tierScore?: number;
};

export type RewardsTierCode = 'DIA' | 'DIA-ELT' | 'DIA-PLS' | 'GLD' | 'PLT' | 'SEV';

export type BlockHistory = {
  createdBy: string;
  blockType: 'BANNED' | 'BARRED' | 'CLOSED' | 'EXTERNAL' | 'SELF EXCLUDED';
  blockLabel: string;
  active: boolean;
  blockStartDate: string;
  blockComment: string;
};

const graphql = gql`
  query card($where: CardWithOperatorInput!) {
    card(where: $where) {
      cardNumber
      cardOperator
      cardStatus
      player {
        playerId
        banned
        barred
        blockHistoryList {
          createdBy
          blockType
          blockLabel
          active
          blockStartDate
          blockComment
        }
        closed
        dateOfBirth
        email
        external
        firstname
        identifications {
          idType
          idLocation
          idNumber
          idExpiration
        }
        rewardPoints
        racingRewardPoints
        selfExcluded
        surname
        primaryIdentifier
        rewardsDetail {
          rcBalance
          tierCode
          tierDesc
          tierScore
        }
      }
    }
  }
`;

export const forTestsOnly = {
  graphql
};
